import { useEffect, useState } from 'react'
import { sendPost } from 'hooks/use-fetch'
import { Box, CircularProgress, Grid, Typography, Button } from '@mui/material'
import AlertMessage from 'Components/AlertMessage'
import { generatePath, useNavigate } from 'react-router'
import { CARENETWORK_MANAGEMENT_DETAILS } from 'Pages/Routes'
import { AccountType, type ChildEditValues, type location } from 'core/types'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import S3Image from 'Components/S3Image'
import { FormatLongDate, BirthdayToAgeInYearsAndMonths } from 'core/Utilities'
import { AccessRequestAction } from 'Pages/CareNetworkManagement/CareNetworkDetail/AccessRequestAction'
import userIcon from 'Assets/Icons/el_adult.png'
import IdentityIcon from '@mui/icons-material/Fingerprint'
import DateRangeIcon from '@mui/icons-material/DateRange'
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined'
import PlaceIcon from '@mui/icons-material/LocationOnOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

interface DuplicateListProps {
  child: ChildEditValues
  disableRequestAccess?: boolean
  onDuplicatesFound?: (dupesFound: boolean) => void
  onLoadChildData?: (childData: ChildSearchResult) => void
}

interface DuplicateChild {
  id: number | null
  firstName: string
  lastName: string
  dateOfBirth: Date
  birthGender: string
  uic: string
  address?: location | null
  requestingISDID: number | null
}

export interface ChildSearchResult {
  id: number
  firstName: string | null
  middleName: string | null
  lastName: string
  nickName: string | null
  suffix: string | null
  isUnborn: boolean
  age: string
  dateOfBirth: Date
  birthGender: string | null
  uic: string | null
  residentISD: string
  residentDistrict: string | null
  isdID: number
  address: string
  city: string
  state: string
  zipCode: string
  language: string | null
  raceList: string[] | null
  imageFile: string | null
  canSelect: boolean
  childNotTiedToISD: boolean
  currentAccessRequestID: number | null
  currentAccessRequestStatus: boolean | null
  childServices: Array<{ programName: string, serviceName: string, serviceProviderName: string, serviceProviderID: number, programID: number, serviceID: number, referralId?: number, enrollmentId?: number }>

}

export function DuplicateList (props: DuplicateListProps): JSX.Element {
  const [duplicateList, setDuplicateList] = useState<ChildSearchResult[]>([])
  const [loaded, setLoaded] = useState(false)
  const nav = useNavigate()

  const duplicateAction = (item: ChildSearchResult): void => {
    if (item.canSelect) {
      const editChild = generatePath(CARENETWORK_MANAGEMENT_DETAILS, { id: item.id.toString() ?? '' })
      nav(editChild)
    }
  }

  const account = useAccountId()

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (props.child.uic != null || (props.child.firstName != null && props.child.lastName != null && props.child.dateOfBirth != null && props.child.dateOfBirth.toString() !== 'Invalid Date' && props.child.birthGender != null)) {
        const child: DuplicateChild = {
          id: props.child.id,
          birthGender: props.child.birthGender,
          dateOfBirth: props.child.dateOfBirth != null ? new Date(props.child.dateOfBirth) : new Date(),
          firstName: props.child.firstName,
          lastName: props.child.lastName,
          uic: props.child.uic ?? '',
          requestingISDID: account?.type === AccountType.ISD ? account.id : (props.child.isdID ?? 0)
        }
        const { response: details } = await sendPost('/ChildDetails/GetDuplicates', child)
        if (details !== null) {
          setDuplicateList(details)
        }
        props.onDuplicatesFound?.(details.length > 0)
      }
      setLoaded(true)
    }
    void fetchData()
  }, [props.child])

  if (!loaded) return <CircularProgress />
  if (duplicateList?.length === 0) return <></>

  return <><Box>
    <AlertMessage type={'warning'} icon={<ErrorOutlineIcon />} message={'Based on the information you have entered, we believe there is a high chance this is a duplicate record.  If you wish continue saving this child, you must acknowledge the duplicates before saving.'} />
    <Grid container flexDirection='row' sx={{ margin: '5px' }} >
      {duplicateList.map((details: ChildSearchResult) => {
        return <Grid item className='CardDisplay' sx={{ backgroundColor: 'rgba(231, 231, 231, 0.2) !important' }}>
          <Typography fontWeight={'bold'} variant='h5'>Child Information</Typography>
          <table className='icon-value-table' >
            <tr>
              <td>
                {details.imageFile != null && details.imageFile !== ''
                  ? <S3Image imageKey={details.imageFile} />
                  : <img src={userIcon} alt='child picture' />
                }
              </td>
              <td>
                <span data-testid='firstNameTextDisplay'>{details.firstName}</span> <span data-testid='middleNameTextDisplay'>{details.middleName}</span> <span data-testid='lastNameTextDisplay'>{details.lastName}</span> <span data-testid='suffixTextDisplay'>{details.suffix}</span>
                {details.nickName !== null && details.nickName !== '' &&
                  <span className="icon-value-table-subitem"> ({details.nickName})</span>
                }
              </td>
            </tr>
            {details.uic !== null && details.uic !== '' &&
              <tr>
                <td><IdentityIcon /></td>
                <td>
                  {details.uic}
                  <span className="icon-value-table-subitem">(UIC)</span></td>
              </tr>
            }
            <tr>
              <td><DateRangeIcon /></td>
              <td>
                <span data-testid='dateOfBirthTextDisplay'>{FormatLongDate(details.dateOfBirth)}</span> <span className="icon-value-table-subitem">({BirthdayToAgeInYearsAndMonths(details.dateOfBirth, false)})</span>
              </td>
            </tr>
            <tr>
              <td><BallotOutlinedIcon /></td>
              <td>
                <div data-testid='birthGenderTextDisplay'>{details.birthGender === 'M' ? 'Male' : details.birthGender === 'F' ? 'Female' : ''}</div>
                <div data-testid='races'>
                  {details.raceList?.join(' / ')}
                </div>
                <div data-testid='primaryLanguageTextDisplay'>Speaks {details.language === null || details.language === '' ? 'English' : details.language}</div>
              </td>
            </tr>
            <tr>
              <td><PlaceIcon /></td>
              <td>
                <div>{details.address}</div>
                <div>{details.city}, {details.state}</div>
                <div>{details.zipCode}</div>
                <div>{details.residentDistrict}</div>
              </td></tr>
          </table>
          <div style={{ textAlign: 'center', width: '100%', marginTop: '5px' }}>
            {!details.canSelect && <AccessRequestAction
              childNotTiedToISD={details.childNotTiedToISD}
              currentAccessRequestID={null}
              currentAccessRequestStatus={null}
              requiresAccessRequest={true}
              childId={details.id}
              changeMade={() => { }}
            />
            }
            {details.canSelect && <Button variant='text' color='primary' onClick={() => { duplicateAction(details) }}>View Details</Button>}
          </div>
        </Grid>
      })}
    </Grid>
  </Box>
  </>
}
